import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const PersonalAssignments = ({ assignments, currentUser }) => {
  const navigate = useNavigate();

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'high':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'medium':
        return 'text-accent bg-accent/10 border-accent/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'text-error bg-error/10';
      case 'in-progress':
        return 'text-warning bg-warning/10';
      case 'investigating':
        return 'text-accent bg-accent/10';
      case 'resolved':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const handleIncidentClick = (incidentId) => {
    navigate(`/incident-details?id=${incidentId}`);
  };

  const handleViewAll = () => {
    navigate(`/incidents-list?assignee=${currentUser?.id}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">My Assignments</h3>
          <p className="text-sm text-muted-foreground">
            {assignments?.length} incident{assignments?.length !== 1 ? 's' : ''} assigned to you
          </p>
        </div>
        <button
          onClick={handleViewAll}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors duration-200"
        >
          <span>View All</span>
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
      {assignments?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={24} className="text-muted-foreground" />
          </div>
          <h4 className="text-lg font-medium text-foreground mb-2">All caught up!</h4>
          <p className="text-sm text-muted-foreground">No incidents assigned to you at the moment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments?.slice(0, 5)?.map((incident) => (
            <div
              key={incident?.id}
              onClick={() => handleIncidentClick(incident?.id)}
              className="p-4 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors duration-200 group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(incident?.severity)}`}>
                      {incident?.severity}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(incident?.status)}`}>
                      {incident?.status}
                    </span>
                  </div>
                  <h4 className="font-medium text-foreground group-hover:text-primary transition-colors duration-200 truncate">
                    {incident?.title}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {incident?.description}
                  </p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-foreground transition-colors duration-200 flex-shrink-0 ml-2" />
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} />
                    <span>Created {formatTimeAgo(incident?.createdAt)}</span>
                  </div>
                  {incident?.slaDeadline && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Timer" size={12} />
                      <span>SLA: {formatTimeAgo(incident?.slaDeadline)}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Hash" size={12} />
                  <span>{incident?.id}</span>
                </div>
              </div>

              {incident?.tags && incident?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {incident?.tags?.slice(0, 3)?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                  {incident?.tags?.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md">
                      +{incident?.tags?.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}

          {assignments?.length > 5 && (
            <div className="text-center pt-4 border-t border-border">
              <button
                onClick={handleViewAll}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
              >
                View {assignments?.length - 5} more assignments
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalAssignments;